/**
 * /investor-detail?id=<investor_profiles.id> — an investor, as a founder sees them.
 *
 * The counterpart to `startup-detail`. Startups have had a founder-facing
 * public page since the beginning; investors never did, so everything the
 * investor profile asks people to write — thesis, sweet spot, deal breakers,
 * how they help, track record — was authored for an audience with no way to
 * reach it.
 *
 * NOT PUBLIC, and not browsable. RLS decides whether this row is readable:
 * a founder sees an investor who has engaged with one of their companies
 * (conversation, data-room request, DD, or holding). An unrelated founder gets
 * no row, which this screen renders as an explicit refusal rather than an
 * empty profile — "nothing here" and "not yours to see" are different, and
 * only one of them is honest.
 *
 * Absence is shown as absence throughout: a section the investor has not
 * filled in is omitted, never faked. That is the same rule the investor's own
 * profile now follows.
 */

import { useState } from 'react';
import { View, Text, ScrollView, ActivityIndicator } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import Animated, { FadeInUp } from 'react-native-reanimated';
import {
  Building2,
  MapPin,
  Target,
  TrendingUp,
  XCircle,
  Sparkles,
  Quote,
  DollarSign,
  Clock,
  Users,
  CheckCircle,
  Star,
  Briefcase,
  MessageSquarePlus,
  Lock,
  Zap,
  Timer,
} from 'lucide-react-native';

import { AppLayout } from '@/components/AppLayout';
import { SectionDivider } from '@/components/SectionDivider';
import AnimatedPressable from '@/components/AnimatedPressable';
import StarRating from '@/components/StarRating';
import TestimonialCard from '@/components/TestimonialCard';
import { useThemeColors } from '@/lib/useThemeColors';
import { useTrackScreen } from '@/lib/analytics';
import { formatCurrency } from '@/lib/utils/format';
import { useAuth } from '@/lib/supabase';
import { useMyStartup } from '@/lib/startup/use-my-startup';
import { useTrackRecord } from '@/lib/supabase/track-record';
import {
  useInvestorPublicProfile,
  RELATIONSHIP_LABELS,
  type InvestorRelationship,
} from '@/lib/investor/related-investors';
import {
  useInvestorEngagementMetrics,
  useInvestorReviews,
  averageRating,
  reviewToTestimonial,
} from '@/lib/investor/profile-signals';
import { useCanReviewInvestor } from '@/lib/investor/write-review';
import { WriteReviewModal } from '@/components/investor/WriteReviewModal';
import { ReportContentModal } from '@/components/moderation/ReportContentModal';
import type { ReportTarget } from '@/lib/moderation/api';

export default function InvestorDetailScreen() {
  useTrackScreen('InvestorDetail');
  const router = useRouter();
  const colors = useThemeColors();

  const { id, relationship } = useLocalSearchParams<{
    id: string;
    relationship?: string;
  }>();

  const { user: authUser } = useAuth();
  const { startupId } = useMyStartup();

  const { data: investor, isLoading } = useInvestorPublicProfile(id);
  const investorUserId = investor?.userId;

  const { data: engagement } = useInvestorEngagementMetrics(investorUserId);
  const { data: reviews = [] } = useInvestorReviews(investorUserId);
  // Gated by RLS *and* by the investor's own show_track_record flag, so an
  // empty list here can mean either "none" or "hidden" — both render the same
  // way, which is the point.
  const { data: trackRecord = [] } = useTrackRecord(investorUserId);
  const { data: canReview = false } = useCanReviewInvestor(investorUserId, startupId ?? undefined);

  const [reviewOpen, setReviewOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState<{ type: ReportTarget; id: string } | null>(null);

  const reviewAverage = averageRating(reviews);
  const myReview = reviews.find((r) => !r.isAnonymous && r.companyName != null);

  if (isLoading) {
    return (
      <AppLayout title="Investor">
        <View className="flex-1 items-center justify-center" style={{ backgroundColor: colors.surface.secondary }}>
          <ActivityIndicator color={colors.primary[600]} />
          <Text className="text-sm mt-3" style={{ color: colors.text.secondary }}>
            Loading investor…
          </Text>
        </View>
      </AppLayout>
    );
  }

  // No row came back. Under RLS that means the caller has no relationship with
  // this investor — say so, rather than showing a blank profile that reads as
  // "this investor has filled nothing in".
  if (!investor) {
    return (
      <AppLayout title="Investor">
        <View
          className="flex-1 items-center justify-center px-8"
          style={{ backgroundColor: colors.surface.secondary }}
        >
          <View
            className="w-14 h-14 rounded-2xl items-center justify-center mb-4"
            style={{ backgroundColor: colors.light[300] }}
          >
            <Lock size={24} color={colors.text.tertiary} />
          </View>
          <Text style={{ color: colors.text.primary }} className="text-lg font-semibold text-center">
            This profile isn&apos;t open to you
          </Text>
          <Text
            style={{ color: colors.text.secondary }}
            className="text-sm text-center mt-2 leading-5"
          >
            You can see an investor&apos;s profile once they engage with your company —
            by starting a conversation, requesting your data room, or running due
            diligence.
          </Text>
          <AnimatedPressable
            onPress={() => router.back()}
            className="mt-6 px-5 py-3 rounded-xl"
            style={{ backgroundColor: colors.primary[600] }}
          >
            <Text className="text-white font-semibold">Go back</Text>
          </AnimatedPressable>
        </View>
      </AppLayout>
    );
  }

  const { profile, name } = investor;
  const rel = relationship as InvestorRelationship | undefined;

  return (
    <AppLayout title={name}>
      <ScrollView
        className="flex-1"
        style={{ backgroundColor: colors.surface.secondary }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: 96 }}
      >
        {/* ── Identity ─────────────────────────────────────────────── */}
        <Animated.View entering={FadeInUp.duration(400)} className="px-5 pt-4">
          <View
            className="rounded-3xl p-6 overflow-hidden"
            style={{
              backgroundColor: colors.surface.primary,
              borderWidth: 1,
              borderColor: colors.border.light,
            }}
          >
            <View className="flex-row items-start">
              <View
                className="w-16 h-16 rounded-2xl items-center justify-center mr-4"
                style={{ backgroundColor: colors.primary[100] }}
              >
                <Text style={{ color: colors.primary[600] }} className="text-2xl font-bold">
                  {name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                </Text>
              </View>

              <View className="flex-1">
                <View className="flex-row items-center gap-2">
                  <Text
                    style={{ color: colors.text.primary }}
                    className="text-xl font-bold flex-1"
                    numberOfLines={1}
                  >
                    {name}
                  </Text>
                  {profile.linkedInVerified && (
                    <CheckCircle size={16} color={colors.traffic.green} />
                  )}
                </View>

                {/* Only what they wrote. No "Angel Investor" default. */}
                {profile.bio?.headline && (
                  <Text
                    style={{ color: colors.text.secondary }}
                    className="text-sm mt-0.5"
                    numberOfLines={2}
                  >
                    {profile.bio.headline}
                  </Text>
                )}

                {profile.firm && (
                  <View className="flex-row items-center gap-1.5 mt-1.5">
                    <Building2 size={12} color={colors.text.tertiary} />
                    <Text style={{ color: colors.text.tertiary }} className="text-sm" numberOfLines={1}>
                      {profile.firm}
                    </Text>
                  </View>
                )}
                {profile.location ? (
                  <View className="flex-row items-center gap-1.5 mt-0.5">
                    <MapPin size={12} color={colors.text.tertiary} />
                    <Text style={{ color: colors.text.tertiary }} className="text-sm" numberOfLines={1}>
                      {profile.location}
                    </Text>
                  </View>
                ) : null}
              </View>
            </View>

            {/* Why this founder can see them. The most useful line on the
                screen: "asked for your data room" and "is on your cap table"
                are very different relationships. */}
            {rel && RELATIONSHIP_LABELS[rel] && (
              <View
                className="flex-row items-center gap-2 mt-4 px-3 py-2 rounded-xl"
                style={{ backgroundColor: colors.primary[50] }}
              >
                <Zap size={14} color={colors.primary[600]} />
                <Text style={{ color: colors.primary[700] }} className="text-sm font-medium" numberOfLines={1}>
                  {RELATIONSHIP_LABELS[rel]}
                </Text>
              </View>
            )}

            {profile.bio?.bio && (
              <Text
                style={{ color: colors.text.secondary }}
                className="text-sm leading-5 mt-4"
              >
                {profile.bio.bio}
              </Text>
            )}
          </View>
        </Animated.View>

        {/* ── How they respond ─────────────────────────────────────── */}
        {(engagement?.responseRate != null || engagement?.medianResponseHours != null) && (
          <Animated.View entering={FadeInUp.delay(80).duration(400)} className="px-5 mt-4">
            <View className="flex-row gap-3">
              {engagement.responseRate != null && (
                <View
                  className="flex-1 rounded-2xl p-4"
                  style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
                >
                  <View className="flex-row items-center gap-2 mb-1.5">
                    <Zap size={14} color={colors.success} />
                    <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
                      Replies to founders
                    </Text>
                  </View>
                  <Text style={{ color: colors.text.primary }} className="text-xl font-bold">
                    {Math.round(engagement.responseRate)}%
                  </Text>
                  {engagement.eligibleThreadCount != null && (
                    <Text style={{ color: colors.text.tertiary }} className="text-xs mt-0.5" numberOfLines={1}>
                      of {engagement.eligibleThreadCount} conversation
                      {engagement.eligibleThreadCount === 1 ? '' : 's'}
                    </Text>
                  )}
                </View>
              )}
              {engagement.medianResponseHours != null && (
                <View
                  className="flex-1 rounded-2xl p-4"
                  style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
                >
                  <View className="flex-row items-center gap-2 mb-1.5">
                    <Timer size={14} color={colors.primary[600]} />
                    <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
                      Typically replies in
                    </Text>
                  </View>
                  <Text style={{ color: colors.text.primary }} className="text-xl font-bold">
                    {engagement.medianResponseHours < 24
                      ? `${Math.round(engagement.medianResponseHours)}h`
                      : `${Math.round(engagement.medianResponseHours / 24)}d`}
                  </Text>
                </View>
              )}
            </View>
          </Animated.View>
        )}

        {/* ── Thesis ───────────────────────────────────────────────── */}
        {profile.thesis && (
          <>
            <View className="px-5">
              <SectionDivider label="Investment Thesis" marginTop={16} marginBottom={12} />
            </View>
            <Animated.View entering={FadeInUp.delay(120).duration(400)} className="px-5">
              <View
                className="rounded-2xl p-5"
                style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
              >
                {profile.thesis.thesisStatement && (
                  <View className="mb-4">
                    <View className="flex-row items-center gap-2 mb-2">
                      <Quote size={15} color={colors.primary[600]} />
                      <Text style={{ color: colors.text.primary }} className="font-semibold">
                        Philosophy
                      </Text>
                    </View>
                    <Text style={{ color: colors.text.secondary }} className="text-sm leading-5">
                      {profile.thesis.thesisStatement}
                    </Text>
                  </View>
                )}

                {profile.thesis.sweetSpot && (
                  <View className="rounded-xl p-4 mb-4" style={{ backgroundColor: colors.primary[50] }}>
                    <View className="flex-row items-center gap-2 mb-1">
                      <Target size={13} color={colors.primary[600]} />
                      <Text style={{ color: colors.primary[700] }} className="text-xs font-medium uppercase">
                        Sweet spot
                      </Text>
                    </View>
                    <Text style={{ color: colors.primary[800] }} className="font-semibold">
                      {profile.thesis.sweetSpot}
                    </Text>
                  </View>
                )}

                <ChipRow
                  icon={<Target size={15} color={colors.primary[600]} />}
                  title="Focus areas"
                  values={profile.thesis.sectors}
                  fg={colors.primary[600]}
                  bg={colors.primary[50]}
                />
                <ChipRow
                  icon={<TrendingUp size={15} color={colors.success} />}
                  title="Stages"
                  values={profile.stagePreference}
                  fg={colors.success}
                  bg={colors.traffic.greenLight}
                  capitalize
                />
                <ChipRow
                  icon={<Users size={15} color={colors.text.secondary} />}
                  title="Looks for"
                  values={profile.thesis.teamPreferences ?? []}
                  fg={colors.text.secondary}
                  bg={colors.light[300]}
                />
                {/* Shown to founders on purpose: knowing what rules someone out
                    saves both sides a meeting. */}
                <ChipRow
                  icon={<XCircle size={15} color={colors.error} />}
                  title="Deal breakers"
                  values={profile.thesis.dealBreakers ?? []}
                  fg={colors.error}
                  bg={colors.red.light}
                />

                {(profile.ticketSize.min > 0 || profile.ticketSize.max > 0) && (
                  <View className="flex-row items-center gap-2 pt-4 mt-2 border-t" style={{ borderColor: colors.border.light }}>
                    <DollarSign size={14} color={colors.text.tertiary} />
                    <Text style={{ color: colors.text.tertiary }} className="text-xs">
                      Cheque size
                    </Text>
                    <Text style={{ color: colors.text.primary }} className="font-semibold ml-auto" numberOfLines={1}>
                      {formatCurrency(profile.ticketSize.min)} – {formatCurrency(profile.ticketSize.max)}
                    </Text>
                  </View>
                )}
              </View>
            </Animated.View>
          </>
        )}

        {/* ── Value-add ────────────────────────────────────────────── */}
        {profile.valueAdd && (
          <>
            <View className="px-5">
              <SectionDivider label="How they help" marginTop={16} marginBottom={12} />
            </View>
            <Animated.View entering={FadeInUp.delay(160).duration(400)} className="px-5">
              <View
                className="rounded-2xl p-5"
                style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
              >
                <ChipRow
                  icon={<Sparkles size={15} color={colors.amber.main} />}
                  title="Expertise"
                  values={profile.valueAdd.expertiseAreas}
                  fg={colors.amber.main}
                  bg={colors.amber.light}
                />
                {profile.valueAdd.description && (
                  <Text style={{ color: colors.text.secondary }} className="text-sm leading-5">
                    {profile.valueAdd.description}
                  </Text>
                )}
                <View className="flex-row gap-3 mt-4">
                  {profile.valueAdd.officeHours && (
                    <View className="flex-1 rounded-xl p-3 items-center" style={{ backgroundColor: colors.traffic.greenLight }}>
                      <Clock size={17} color={colors.success} />
                      <Text style={{ color: colors.success }} className="text-xs font-medium mt-1" numberOfLines={1}>
                        Offers office hours
                      </Text>
                    </View>
                  )}
                  {profile.valueAdd.boardExperience ? (
                    <View className="flex-1 rounded-xl p-3 items-center" style={{ backgroundColor: colors.primary[50] }}>
                      <Users size={17} color={colors.primary[600]} />
                      <Text style={{ color: colors.primary[600] }} className="font-bold mt-1">
                        {profile.valueAdd.boardExperience}
                      </Text>
                      <Text style={{ color: colors.primary[600] }} className="text-xs" numberOfLines={1}>
                        Board seats
                      </Text>
                    </View>
                  ) : null}
                </View>
              </View>
            </Animated.View>
          </>
        )}

        {/* ── Track record ─────────────────────────────────────────── */}
        {trackRecord.length > 0 && (
          <>
            <View className="px-5">
              <SectionDivider label="Track record" icon={Briefcase} marginTop={16} marginBottom={12} />
            </View>
            <Animated.View entering={FadeInUp.delay(200).duration(400)} className="px-5">
              <View
                className="rounded-2xl overflow-hidden"
                style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
              >
                {trackRecord.slice(0, 8).map((record, index) => (
                  <View key={`track-${index}`}>
                    {index > 0 && (
                      <View className="mx-4" style={{ height: 1, backgroundColor: colors.border.light }} />
                    )}
                    <View className="flex-row items-center px-4 py-3">
                      <View
                        className="w-9 h-9 rounded-xl items-center justify-center mr-3"
                        style={{ backgroundColor: colors.primary[50] }}
                      >
                        <Building2 size={16} color={colors.primary[600]} />
                      </View>
                      <View className="flex-1">
                        <Text style={{ color: colors.text.primary }} className="font-medium" numberOfLines={1}>
                          {record.companyName}
                        </Text>
                        <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
                          {record.stage} · {record.date.split('-')[0]}
                        </Text>
                      </View>
                    </View>
                  </View>
                ))}
                {trackRecord.length > 8 && (
                  <Text
                    style={{ color: colors.text.tertiary }}
                    className="text-xs text-center py-3"
                  >
                    + {trackRecord.length - 8} more
                  </Text>
                )}
              </View>
            </Animated.View>
          </>
        )}

        {/* ── Founder reviews ──────────────────────────────────────── */}
        {(reviews.length > 0 || canReview) && (
          <>
            <View className="px-5">
              <SectionDivider label="Founder reviews" icon={Star} marginTop={16} marginBottom={12} />
            </View>
            <Animated.View entering={FadeInUp.delay(240).duration(400)} className="px-5">
              <View
                className="rounded-2xl p-5"
                style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
              >
                {reviewAverage != null ? (
                  <>
                    <View className="flex-row items-center gap-2">
                      <Text style={{ color: colors.text.primary }} className="text-3xl font-bold">
                        {reviewAverage.toFixed(1)}
                      </Text>
                      <StarRating rating={reviewAverage} size="md" />
                    </View>
                    <Text style={{ color: colors.text.tertiary }} className="text-sm mt-1 mb-4" numberOfLines={1}>
                      From {reviews.length} founder{reviews.length === 1 ? '' : 's'} who worked with them
                    </Text>
                    <View className="gap-3">
                      {reviews.slice(0, 3).map((review) => (
                        <TestimonialCard
                          key={review.id}
                          testimonial={reviewToTestimonial(review)}
                          compact
                          onReport={() => setReportTarget({ type: 'review', id: review.id })}
                        />
                      ))}
                    </View>
                  </>
                ) : (
                  <Text style={{ color: colors.text.tertiary }} className="text-sm">
                    No founder has reviewed this investor yet.
                  </Text>
                )}

                {/* Offered only when the server says this startup is eligible —
                    the same predicate the INSERT policy uses. */}
                {canReview && (
                  <AnimatedPressable
                    onPress={() => setReviewOpen(true)}
                    className="flex-row items-center justify-center gap-2 py-3 rounded-xl mt-4"
                    style={{ backgroundColor: colors.primary[600] }}
                  >
                    <MessageSquarePlus size={17} color="white" />
                    <Text className="text-white font-semibold">
                      {myReview ? 'Edit your review' : 'Write a review'}
                    </Text>
                  </AnimatedPressable>
                )}
              </View>
            </Animated.View>
          </>
        )}
      </ScrollView>

      {investorUserId && startupId && authUser?.id && (
        <WriteReviewModal
          visible={reviewOpen}
          onClose={() => setReviewOpen(false)}
          investorUserId={investorUserId}
          investorName={name}
          startupId={startupId}
          authorUserId={authUser.id}
        />
      )}
      <ReportContentModal visible={!!reportTarget} onClose={() => setReportTarget(null)} target={reportTarget} />
    </AppLayout>
  );
}

/**
 * A labelled row of chips that renders nothing at all when the list is empty.
 *
 * Every list on this screen belongs to someone else, so "they left this blank"
 * has to look like absence rather than an empty box with a heading.
 */
function ChipRow({
  icon,
  title,
  values,
  fg,
  bg,
  capitalize = false,
}: {
  icon: React.ReactNode;
  title: string;
  values: string[];
  fg: string;
  bg: string;
  capitalize?: boolean;
}) {
  const colors = useThemeColors();
  if (!values || values.length === 0) return null;

  return (
    <View className="mb-4">
      <View className="flex-row items-center gap-2 mb-2.5">
        {icon}
        <Text style={{ color: colors.text.primary }} className="font-semibold" numberOfLines={1}>
          {title}
        </Text>
      </View>
      <View className="flex-row flex-wrap gap-2">
        {values.map((value, index) => (
          <View
            key={`${title}-${index}`}
            className="px-3 py-1.5 rounded-lg"
            style={{ backgroundColor: bg, flexShrink: 1 }}
          >
            <Text
              style={{ color: fg }}
              className={`text-sm font-medium${capitalize ? ' capitalize' : ''}`}
              numberOfLines={1}
            >
              {capitalize ? value.replace(/-/g, ' ') : value}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}
